<script>
  import { onMount } from "svelte";
  import "./app.css";
  import {
    loadOfficialDaSources,
    savedCalculations,
    customDaSources,
    toasts,
    showToast,
  } from "./lib/stores.js";
  import HomeView from "./lib/HomeView.svelte";
  import CalculatorView from "./lib/CalculatorView.svelte";
  import DaManagerView from "./lib/DaManagerView.svelte";
  import Modal from "./lib/Modal.svelte";

  let currentView = "home";
  let activeCalculationId = null;

  // --- Modal State ---
  let showDataManagementModal = false;
  let showDaManagerModal = false;
  let showPrivacyModal = false;
  let dataManagementExport = "";
  let dataManagementImport = "";

  const customDaPlaceholder = `{
  "My Pension Rules": [
    { "type": "pensioner", "effective_date": "2023-01-01", "to": 38 }
  ]
}`;

  onMount(() => {
    console.log("[App] App is mounting. Loading official DA sources...");
    loadOfficialDaSources();
    console.log(
      "[App] Initial saved calculations from localStorage:",
      $savedCalculations
    );

    // Debug DA sources after loading
    setTimeout(() => {
      import("./lib/stores.js").then(({ debugDaSources }) => {
        debugDaSources();
      });
    }, 1000);
  });

  // This is the function we will pass down to our child components
  function navigateTo(view, calcId = null) {
    console.log(`[App] Navigating to view: '${view}' with ID: ${calcId}`);
    activeCalculationId = calcId;
    currentView = view;
    window.scrollTo(0, 0);
  }

  function openDataModal() {
    console.log("[App] Opening Data Management Modal.");
    dataManagementExport = JSON.stringify($savedCalculations, null, 2);
    dataManagementImport = "";
    showDataManagementModal = true;
  }

  function downloadBackup() {
    const timestamp = new Date()
      .toISOString()
      .replace(/[:.]/g, "-")
      .slice(0, 19);
    const backupData = {
      timestamp: new Date().toISOString(),
      version: "1.0",
      calculations: $savedCalculations,
      metadata: {
        totalCalculations: $savedCalculations.length,
        exportDate: new Date().toLocaleString(),
        appVersion: "Salary Breakdown Pro v1.0",
      },
    };

    const blob = new Blob([JSON.stringify(backupData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `salary-breakdown-pro-backup-${timestamp}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    showToast("Backup downloaded successfully!", "success");
  }

  function handleImportCalculations() {
    try {
      if (!dataManagementImport.trim())
        throw new Error("Import field is empty.");
      const data = JSON.parse(dataManagementImport);
      let calculations;
      if (Array.isArray(data)) {
        calculations = data;
      } else if (data.calculations && Array.isArray(data.calculations)) {
        calculations = data.calculations;
        showToast(
          `Importing backup from ${data.metadata?.exportDate || "unknown date"}`,
          "info"
        );
      } else if (
        typeof data === "object" &&
        data !== null &&
        data.id &&
        data.name
      ) {
        // Single card import
        calculations = [...$savedCalculations, data];
      } else {
        throw new Error(
          "Invalid format. Expected an array of calculations, backup file, or single card."
        );
      }
      // Check for duplicates by name
      const existingNames = new Set($savedCalculations.map((c) => c.name));
      const duplicates = calculations.filter((c) => existingNames.has(c.name));
      if (duplicates.length > 0) {
        if (
          !confirm(
            `Some cards already exist: ${duplicates.map((d) => d.name).join(", ")}. Overwrite? (Cancel to keep both)`
          )
        ) {
          // Keep both: rename imported cards
          calculations = calculations.map((c) => {
            if (existingNames.has(c.name)) {
              return { ...c, name: c.name + " (Imported)" };
            }
            return c;
          });
        } else {
          // Overwrite: remove existing with same name
          calculations = [
            ...$savedCalculations.filter(
              (c) => !duplicates.some((d) => d.name === c.name)
            ),
            ...calculations.filter(
              (c) =>
                duplicates.some((d) => d.name === c.name) ||
                !existingNames.has(c.name)
            ),
          ];
        }
      } else {
        calculations = [...$savedCalculations, ...calculations];
      }
      savedCalculations.set(calculations);
      showToast("Calculations imported successfully!", "success");
      showDataManagementModal = false;
    } catch (e) {
      showToast(`Import failed: ${e.message}`, "error");
    }
  }

  function handleClearAllData() {
    if (
      confirm(
        "Are you sure you want to delete ALL saved calculations? This cannot be undone."
      )
    ) {
      savedCalculations.set([]);
      showToast("All calculations cleared.", "info");
      showDataManagementModal = false;
    }
  }

  const currentYear = new Date().getFullYear();
</script>

<!-- Toast Notifications Container -->
{#if $toasts.length > 0}
  <div
    style="position: fixed; bottom: 20px; left: 50%; transform: translateX(-50%); z-index: 1200; display: flex; flex-direction: column; gap: 10px;"
  >
    {#each $toasts as toast (toast.id)}
      <div
        id="toast"
        class:show={true}
        style="background-color: var(--{toast.type}-color); position: relative; bottom: 0; transform: none; visibility: visible; opacity: 1;"
      >
        {toast.message}
      </div>
    {/each}
  </div>
{/if}

<div class="app-container">
  <main>
    {#if currentView === "home"}
      <HomeView
        onNavigate={navigateTo}
        onOpenDataModal={openDataModal}
        onOpenDaManager={() => (showDaManagerModal = true)}
      />
    {:else if currentView === "calculator"}
      <CalculatorView
        onNavigate={navigateTo}
        calculationId={activeCalculationId}
      />
    {/if}
  </main>

  <footer class="app-footer">
    <div class="footer-content">
      <p class="copyright">© {currentYear} Appsphinx Salary Breakdown Pro</p>
      <button
        class="privacy-btn"
        on:click={() => (showPrivacyModal = true)}
        title="View Privacy Policy"
      >
        Privacy Policy
      </button>
    </div>
  </footer>
</div>

<!-- DATA MANAGEMENT MODAL -->
<Modal
  bind:show={showDataManagementModal}
  on:close={() => (showDataManagementModal = false)}
>
  <h3 slot="header">Manage Calculations</h3>
  <div slot="body">
    <div class="backup-section">
      <h4>📦 Create Backup</h4>
      <p class="section-description">
        Download a complete backup of all your calculations with timestamp
      </p>
      <button
        on:click={downloadBackup}
        class="btn btn-primary"
        style="width: 100%; margin-bottom: 20px;"
      >
        📥 Download Backup ({$savedCalculations.length} calculations)
      </button>
    </div>

    <hr style="margin: 20px 0; border-color: var(--glass-border);" />

    <div class="export-section">
      <h4>📋 Export Calculations</h4>
      <p class="section-description">Copy all calculations to clipboard</p>
      <textarea readonly bind:value={dataManagementExport}></textarea>
      <button
        on:click={() => navigator.clipboard.writeText(dataManagementExport)}
        class="btn btn-secondary"
        style="margin-top: 10px; width: 100%;"
      >
        📋 Copy to Clipboard
      </button>
    </div>

    <hr style="margin: 20px 0; border-color: var(--glass-border);" />

    <div class="import-section">
      <h4>📤 Import Calculations</h4>
      <p class="section-description">
        Import from backup file or paste JSON data (will overwrite existing)
      </p>
      <textarea
        placeholder="Paste your backup JSON here..."
        bind:value={dataManagementImport}
      ></textarea>
      <div
        class="action-buttons"
        style="flex-direction: row; justify-content: space-between; margin-top: 10px;"
      >
        <button on:click={handleImportCalculations} class="btn btn-primary">
          📤 Import
        </button>
        <button on:click={handleClearAllData} class="btn btn-danger">
          🗑️ Clear All Data
        </button>
      </div>
    </div>
  </div>
</Modal>

<!-- DA MANAGER MODAL -->
<Modal
  bind:show={showDaManagerModal}
  on:close={() => (showDaManagerModal = false)}
>
  <h3 slot="header">DA Manager</h3>
  <div slot="body">
    <DaManagerView />
  </div>
</Modal>

<!-- Privacy Policy Modal -->
<Modal bind:show={showPrivacyModal} on:close={() => (showPrivacyModal = false)}>
  <h3 slot="header">Privacy Policy</h3>
  <div slot="body" class="privacy-content">
    <h4>Your Data Privacy</h4>
    <p>
      Salary Breakdown Pro is committed to protecting your privacy. Our
      application:
    </p>
    <ul>
      <li>Does not collect any personal information</li>
      <li>Stores all calculation data locally on your device only</li>
      <li>Does not track your usage or behavior</li>
      <li>Does not use cookies or any tracking mechanisms</li>
      <li>Does not share any data with third parties</li>
    </ul>

    <h4>Local Storage</h4>
    <p>
      All your calculations and settings are stored locally in your browser's
      storage. This means:
    </p>
    <ul>
      <li>Your data never leaves your device</li>
      <li>You have complete control over your data</li>
      <li>
        You can clear all data at any time using the app's data management
        features
      </li>
    </ul>

    <h4>Contact</h4>
    <p>
      For any privacy concerns or questions, please visit <a
        href="https://appsphinx.com"
        target="_blank"
        rel="noopener noreferrer">Appsphinx.com</a
      >
    </p>
  </div>
</Modal>

<style>
  .backup-section,
  .export-section,
  .import-section {
    margin-bottom: 20px;
  }

  .section-description {
    color: var(--text-muted);
    font-size: 0.9rem;
    margin: 8px 0 15px 0;
    line-height: 1.4;
  }

  .backup-section h4,
  .export-section h4,
  .import-section h4 {
    margin: 0 0 8px 0;
    color: var(--text-color);
    font-size: 1.1rem;
  }

  textarea {
    width: 100%;
    min-height: 120px;
    padding: 12px;
    border: 1px solid var(--glass-border);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.05);
    color: var(--text-color);
    font-family: "Courier New", monospace;
    font-size: 0.85rem;
    resize: vertical;
  }

  textarea:focus {
    outline: 2px solid var(--primary-color);
    border-color: var(--primary-color);
  }

  .action-buttons {
    display: flex;
    gap: 10px;
  }

  .action-buttons button {
    flex: 1;
  }

  @media (max-width: 768px) {
    .action-buttons {
      flex-direction: column;
    }
  }

  .main-nav {
    display: flex;
    gap: 16px;
    margin-top: 10px;
    margin-bottom: 10px;
  }
  .nav-btn {
    background: var(--primary-color, #22c55e);
    color: #fff;
    border: none;
    border-radius: 6px;
    padding: 8px 18px;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s;
  }
  .nav-btn:hover,
  .nav-btn:focus {
    background: var(--primary-color-dark, #16a34a);
    outline: 2px solid var(--primary-color);
  }

  .app-container {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
  }

  .app-footer {
    margin-top: auto;
    padding: 1rem;
    background: var(--glass-bg);
    border-top: 1px solid var(--glass-border);
  }

  .footer-content {
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 1rem;
  }

  .copyright {
    color: var(--text-muted);
    font-size: 0.9rem;
    margin: 0;
  }

  .privacy-btn {
    background: none;
    border: none;
    color: var(--text-muted);
    font-size: 0.9rem;
    cursor: pointer;
    padding: 0.5rem;
    transition: color 0.2s;
  }

  .privacy-btn:hover {
    color: var(--text-color);
    text-decoration: underline;
  }

  .privacy-content {
    color: var(--text-color);
    line-height: 1.6;
  }

  .privacy-content h4 {
    margin: 1.5rem 0 0.5rem;
    color: var(--text-color);
  }

  .privacy-content p {
    margin: 0.5rem 0;
  }

  .privacy-content ul {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
  }

  .privacy-content a {
    color: var(--primary-color);
    text-decoration: none;
  }

  .privacy-content a:hover {
    text-decoration: underline;
  }
</style>
